import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import HelpfulArticles from '../pages/HelpfulArticles';
import HelpfulArticle from '../pages/HelpfulArticle';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import WorkerFaq from './WorkerFaq';
import './marketing.scss';
import './worker-faq.scss';

const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const url = path =>
  path.startsWith('http') || path.startsWith('mailto:') || path.startsWith('#')
    ? path
    : base + path;
const image = name => url('/marketing/' + name);
const jobs = 'https://jobs.spurstaffing.com/';
const handbook =
  'https://spurjobs-public.s3.amazonaws.com/Spur+Employment+Handbook+Federal+-+English.pdf';
const customerSuccessDescription =
  'Support school district partners and their hourly employees.';
const educationTeam = [
  [
    'Crystal Difrancesca',
    'VP of HR',
    'Leads human resources, including onboarding and compliance.',
  ],
  [
    'Jennifer Haley',
    'VP of Accounting',
    'Works with customers on invoicing, payroll, and billing.',
  ],
  ['Mary Gast', 'Customer Success Specialist', customerSuccessDescription],
  ['Vicky Lawrence', 'Customer Success Specialist', customerSuccessDescription],
  ['Nicole Spann', 'Customer Success Specialist', customerSuccessDescription],
  ['Amy Flores', 'Customer Success Specialist', customerSuccessDescription],
];
const afterschoolTeam = [
  ['Dr. Camille Wright', 'Executive Director of Student Achievement'],
  ['Tiffanye Mitchell', 'Head of School District Partnerships'],
  ['Darren Douthitt', 'Education Program Manager'],
  ['Ivy Murry', 'Education Program Manager'],
  ['Tracy Penney', 'Education Program Manager'],
  ['Allen Malone', 'Education Program Manager'],
  ['Veronica Haley', 'Education Program Manager'],
  ['Jennifer Haley', 'VP of Accounting'],
];

const articles = [
  {
    page: 'lessons',
    path: '/lessons-after-the-bell/',
    category: 'White papers',
    title: 'Lessons After The Bell',
    image: 'lessons-after-bell.jpg',
    intro:
      'The remarkable impact of after-school and summer programs on student achievement.',
    sections: [
      [
        'Addressing learning loss',
        'Seasonal breaks and disruptions to classroom learning can make it harder for students to build foundational reading and math skills. Extended learning programs give students more time and individual support.',
      ],
      [
        'Extending learning',
        'Afterschool Labs combines academic support and hands-on activities in reading, math, science, technology, engineering, and the arts. Its programs are designed to keep students engaged while addressing specific learning needs.',
      ],
      [
        'Evaluating the impact',
        'An independent evaluation compared students participating in Afterschool Labs programs with their peers across three Alabama school systems. The findings showed stronger growth in several reading and math measures for program participants.',
      ],
    ],
  },
  {
    page: 'case-study',
    path:
      '/case-study-14th-largest-school-district-overcomes-staffing-challenges-in-their-child-nutrition-program/',
    category: 'Case studies',
    title:
      '14th largest school district overcomes staffing challenges in their child nutrition program',
    image: 'case-study.jpg',
    intro:
      'How Spur helped a large district recruit and support cafeteria nutrition assistants across 139 schools.',
    sections: [
      [
        'The staffing challenge',
        'A district serving 177,000 students needed a dependable way to staff its child nutrition program and keep meals available every school day.',
      ],
      [
        'The staffing solution',
        'Spur recruited within the communities served by the district and helped candidates complete the required screening and training. Within a month, more than 240 assistants were ready to work.',
      ],
      [
        'The impact',
        'The district gained real-time visibility into staffing vacancies and a way to fill them quickly, while reducing costs and improving consistency across its cafeterias.',
      ],
    ],
  },
  {
    page: 'temporary-work',
    path: '/career-benefits-of-temporary-work-blog/',
    category: 'Blog',
    title:
      'The Gateway to Growth: Exploring Professional Advantages of Temporary Work',
    image: 'blog.jpg',
    intro:
      'Temporary work can offer a useful path to new skills, professional connections, and future opportunities.',
    sections: [
      [
        'Build transferable skills',
        'Short-term roles in education, customer service, hospitality, and other fields can strengthen communication, adaptability, and problem-solving skills.',
      ],
      [
        'Explore new directions',
        'Temporary assignments let people test different settings and responsibilities while gaining experience and growing their networks.',
      ],
      [
        'Find your next role',
        'Spur connects qualified school support staff with opportunities that fit their experience and availability.',
      ],
    ],
  },
  {
    page: 'achievement',
    path:
      '/white-paper-accelerate-student-achievement-by-20-higher-than-average-with-this-after-school-program/',
    category: 'White papers',
    title:
      'Accelerate student achievement by 20%+ higher than average with this after school program',
    image: 'afterschool-study.jpg',
    intro:
      'A closer look at extended learning programs and their effect on reading and math growth.',
    sections: [
      [
        'A focused approach',
        'Afterschool Labs provides academic support, engaging activities, staffing, training, and program administration in one solution.',
      ],
      [
        'Measuring growth',
        'Program evaluations compare student achievement and attendance with district peers to help leaders understand what is working.',
      ],
      [
        'Support for districts',
        'The program helps schools create more learning time while reducing the operational work required to run after-school and summer offerings.',
      ],
    ],
  },
];

function Button({ href, children, outline = false, light = false }) {
  return (
    <a
      className={
        'm-button' +
        (outline ? ' m-button--outline' : '') +
        (light ? ' m-button--light' : '')
      }
      href={url(href)}
    >
      {children}
      <span aria-hidden="true"> →</span>
    </a>
  );
}

function Header({ transparent }) {
  const [open, setOpen] = useState(false);
  return (
    <header
      className={'m-header' + (transparent ? ' m-header--transparent' : '')}
    >
      <div className="m-header__inner">
        <a href={url('/')} className="m-brand" aria-label="Spur home">
          <img src={image('logo.webp')} alt="Spur" />
        </a>
        <nav className="m-nav" aria-label="Primary navigation">
          <div className="m-nav__group">
            <a href={url('/services/education-staffing/')}>Services</a>
            <div className="m-nav__submenu">
              <a href={url('/services/education-staffing/')}>
                Education Staffing
              </a>
              <a href={url('/services/afterschool-programming/')}>
                After-School Programming
              </a>
            </div>
          </div>
          <a href={jobs}>Jobs</a>
          <a href={url('/resources/')}>Resources</a>
          <a href={url('/worker-faq/')}>Worker FAQ</a>
          <a href={url('/contact-us/')}>Contact Us</a>
        </nav>
        <button
          className="m-menu-button"
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
      {open && (
        <nav className="m-mobile-nav" aria-label="Mobile navigation">
          <a href={url('/services/education-staffing/')}>Education Staffing</a>
          <a href={url('/services/afterschool-programming/')}>
            After-School Programming
          </a>
          <a href={jobs}>Jobs</a>
          <a href={url('/resources/')}>Resources</a>
          <a href={url('/worker-faq/')}>Worker FAQ</a>
          <a href={url('/contact-us/')}>Contact Us</a>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="m-footer">
      <div className="m-container m-footer__grid">
        <div>
          <a href={url('/')}>
            <img
              src={image('logo.webp')}
              alt="Spur"
              className="m-footer__logo"
            />
          </a>
          <p>Tech-forward staffing for people-focused school districts.</p>
          <div className="m-social">
            <a
              href="https://www.facebook.com/spurstaffing"
              aria-label="Facebook"
            >
              Facebook
            </a>
            <a
              href="https://www.linkedin.com/company/spurwork"
              aria-label="LinkedIn"
            >
              LinkedIn
            </a>
            <a
              href="https://www.instagram.com/spurstaffing/"
              aria-label="Instagram"
            >
              Instagram
            </a>
          </div>
        </div>
        <div>
          <h3>Menu</h3>
          <a href={url('/services/education-staffing/')}>Education Staffing</a>
          <a href={url('/services/afterschool-programming/')}>
            After-School Programming
          </a>
          <a href={jobs}>Jobs</a>
          <a href={url('/resources/')}>Resources</a>
          <a href={url('/worker-faq/')}>Worker FAQ</a>
          <a href={url('/contact-us/')}>Contact Us</a>
        </div>
        <div>
          <h3>Contact</h3>
          <p>
            Email
            <br />
            <a href="mailto:support@spurstaffing.com">
              support@spurstaffing.com
            </a>
          </p>
          <p>
            Fax
            <br />
            1-256-382-0968
          </p>
        </div>
        <div>
          <h3>Resources</h3>
          <a href={handbook}>Employee Handbook</a>
          <a href={url('/worker-faq/')}>Worker Support FAQ</a>
          <a href={url('/labor-law-posters/')}>Labor Law Posters</a>
          <a href={url('/terms-of-service/')}>Terms of Service</a>
          <a href={url('/privacy-policy-2/')}>Privacy Policy</a>
          <a href={url('/delete-account/')}>Delete your Spur account</a>
        </div>
      </div>
      <div className="m-footer__bottom">
        © {new Date().getFullYear()} Spur Staffing
      </div>
    </footer>
  );
}

function ContactForm() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const endpoint = process.env.REACT_APP_CONTACT_FORM_ENDPOINT;

  const handleSubmit = async event => {
    event.preventDefault();
    if (status === 'sending') return;
    if (!endpoint) {
      setError('This form is temporarily unavailable. Please try again later.');
      return;
    }

    const form = event.currentTarget;
    const fields = { sourcePage: window.location.pathname };
    new FormData(form).forEach((value, name) => {
      fields[name] = value;
    });
    setStatus('sending');
    setError('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!response.ok) throw new Error('Submission failed');
      form.reset();
      setStatus('sent');
    } catch (e) {
      setError('We could not send your message. Please try again later.');
      setStatus('idle');
    }
  };

  return (
    <section id="contact" className="m-contact">
      <div className="m-container m-contact__grid">
        <div>
          <p className="m-eyebrow">Let's talk</p>
          <h2>Contact Spur</h2>
          <p>
            We’d love to learn more about you and your education staffing needs.
            Tell us a little about your organization, and our team will connect
            with you shortly.
          </p>
          <p>
            Looking for work? <a href={jobs}>Browse open jobs →</a>
          </p>
        </div>
        <div className="m-contact__form">
          {status === 'sent' ? (
            <div className="m-contact__success" role="status">
              Thank you. Your message has been sent to the Spur team.
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="m-contact__fields">
                <label>
                  First name *
                  <input
                    name="firstName"
                    autoComplete="given-name"
                    required
                    maxLength="100"
                  />
                </label>
                <label>
                  Last name *
                  <input
                    name="lastName"
                    autoComplete="family-name"
                    required
                    maxLength="100"
                  />
                </label>
                <label>
                  Email *
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength="254"
                  />
                </label>
                <label>
                  Phone number *
                  <input
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    maxLength="50"
                  />
                </label>
                <label className="m-contact__field-wide">
                  I'm a... *
                  <select name="reason" required defaultValue="">
                    <option value="" disabled>
                      Select one
                    </option>
                    <option value="School district, company, or organization looking for a staffing solution">
                      School district, company, or organization looking for a
                      staffing solution
                    </option>
                    <option value="Person looking for work">
                      Person looking for work
                    </option>
                    <option value="Person interested in learning more">
                      Person interested in learning more
                    </option>
                  </select>
                </label>
                <label>
                  Job title *
                  <input
                    name="jobTitle"
                    autoComplete="organization-title"
                    required
                    maxLength="150"
                  />
                </label>
                <label>
                  Organization name *
                  <input
                    name="organization"
                    autoComplete="organization"
                    required
                    maxLength="150"
                  />
                </label>
                <label className="m-contact__field-wide">
                  Organization website
                  <input
                    name="organizationWebsite"
                    type="url"
                    placeholder="https://example.org"
                    maxLength="500"
                  />
                </label>
                <label className="m-contact__field-wide">
                  How can we help?
                  <textarea name="message" rows="4" maxLength="5000" />
                </label>
              </div>
              <div className="m-contact__honeypot" aria-hidden="true">
                <label>
                  Leave this field empty
                  <input
                    name="websiteConfirmation"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>
              </div>
              {error && (
                <p className="m-contact__error" role="alert">
                  {error}
                </p>
              )}
              <button
                className="m-contact__submit"
                type="submit"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Hero({
  eyebrow,
  title,
  text,
  background,
  home = false,
  accent = false,
}) {
  return (
    <section
      className={
        'm-hero' +
        (home ? ' m-hero--home' : '') +
        (accent ? ' m-hero--orange' : '')
      }
      style={{
        backgroundImage:
          'linear-gradient(90deg, rgba(20,19,26,.76), rgba(20,19,26,.30)), url(' +
          image(background) +
          ')',
      }}
    >
      <div className="m-container m-hero__content">
        {eyebrow && <p className="m-eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        <p>{text}</p>
        <div className="m-actions">
          <Button href="/contact-us/#contact" outline light>
            Get Started
          </Button>
          <Button href="#learn-more" light>
            Learn More
          </Button>
        </div>
      </div>
    </section>
  );
}

function ArticleCards({ compact = false }) {
  return (
    <div className={'m-articles' + (compact ? ' m-articles--compact' : '')}>
      {articles.map(article => (
        <article className="m-article-card" key={article.page}>
          <a href={url(article.path)}>
            <img src={image(article.image)} alt="" loading="lazy" />
          </a>
          <div>
            <p className="m-eyebrow">{article.category}</p>
            <h3>
              <a href={url(article.path)}>{article.title}</a>
            </h3>
            <a className="m-text-link" href={url(article.path)}>
              Read Article →
            </a>
          </div>
        </article>
      ))}
    </div>
  );
}

function Team({ title, text, people }) {
  return (
    <section className="m-section m-section--lavender">
      <div className="m-container">
        <div className="m-section__intro">
          <p className="m-eyebrow">The people behind the work</p>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
        <div className="m-team-grid">
          {people.map(([name, role, bio]) => (
            <div className="m-team-card" key={name}>
              <div className="m-team-card__mark" aria-hidden="true">
                {name
                  .split(' ')
                  .filter(part => !part.includes('.'))
                  .map(part => part[0])
                  .slice(0, 2)
                  .join('')}
              </div>
              <h3>{name}</h3>
              <p className="m-team-card__role">{role}</p>
              {bio && <p>{bio}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Home() {
  return (
    <>
      <Hero
        home
        title={
          <>
            Tech-Forward Staffing
            <br />
            for People-Focused School Districts
          </>
        }
        text="People are our passion. That’s why we’ve developed education staffing software and services that provide our school district partners with more time and money to invest in what truly makes a difference: the exceptional people who support their school communities."
        background="home-hero.jpg"
      />
      <section id="learn-more" className="m-split m-container">
        <div className="m-split__image">
          <img
            src={image('staffing-software.webp')}
            alt="Staffing software on a laptop"
            loading="lazy"
          />
        </div>
        <div>
          <p className="m-eyebrow">What we do</p>
          <h2>Staffing Services</h2>
          <p>
            At Spur, we’ve raised the bar on staffing hourly jobs in schools.
            With our proprietary staffing software, tech-forward approach, and
            focus on employee satisfaction, we deliver strong staffing results
            at a more affordable price.
          </p>
          <ul className="m-checks">
            <li>Increased fill rates</li>
            <li>Higher retention rates</li>
            <li>Happier hourly employees</li>
          </ul>
          <p>
            Whether you need on-demand staff or long-term placements, we’ve got
            you covered.
          </p>
          <Button href="/services/education-staffing/">
            Explore Education Staffing
          </Button>
        </div>
      </section>
      <section className="m-split m-split--reverse m-container">
        <div className="m-split__image">
          <img
            src={image('job-opportunities.webp')}
            alt="Flexible work opportunities"
            loading="lazy"
          />
        </div>
        <div>
          <p className="m-eyebrow">Work with us</p>
          <h2>Job Opportunities</h2>
          <p>
            From substitutes and special education aides to custodians and
            cafeteria workers, Spur connects qualified school support staff with
            districts ready to work with them.
          </p>
          <p>
            We offer competitive hourly wages, comprehensive benefits, career
            coaching, and easier access to work.
          </p>
          <Button href={jobs}>Join The Team</Button>
        </div>
      </section>
      <section className="m-section m-section--lavender">
        <div className="m-container">
          <div className="m-section__intro">
            <p className="m-eyebrow">Insights and stories</p>
            <h2>Resources from Spur</h2>
            <p>
              Explore the ideas, results, and people behind our work in schools.
            </p>
          </div>
          <ArticleCards compact />
          <div className="m-center">
            <Button href="/resources/">View All Resources</Button>
          </div>
        </div>
      </section>
      <ContactForm />
    </>
  );
}

function Education() {
  const roles = [
    'Bus Aides',
    'Bus Drivers',
    'Cafeteria Nutrition Workers',
    'Certified Substitutes',
    'Custodians',
    'Instructional Aides',
    'Licensed Practical Nurses',
    'Non-Certified Substitutes',
    'Paraprofessionals',
    'Security Guards',
    'Special Education Aides',
    'Teacher Aides',
  ];
  const services = [
    'Recruitment',
    'Time + Attendance',
    'Benefits Administration',
    'Payroll',
    'Onboarding + Training',
    'Assignment Management',
    'Coaching + Development',
    'HR Support',
  ];
  return (
    <>
      <Hero
        eyebrow="Education"
        title="Staffing for a brighter future."
        text="Every member of your team makes a difference in the lives of your students. Our education staffing software and services help ensure the support staff assisting your teachers, feeding your students, and cleaning your hallways are as reliable and passionate as you are."
        background="education-hero.jpg"
      />
      <section id="learn-more" className="m-section m-container">
        <div className="m-section__intro">
          <p className="m-eyebrow">Full-service support</p>
          <h2>Education Staffing Solutions</h2>
          <p>
            We oversee every aspect of the employee lifecycle, from recruitment
            to retention. Whether you need on-demand, short-term, or long-term
            positions, Spur can help.
          </p>
        </div>
        <div className="m-pill-grid">
          {services.map(item => (
            <div key={item}>{item}</div>
          ))}
        </div>
      </section>
      <section className="m-section m-section--lavender">
        <div className="m-container">
          <div className="m-section__intro">
            <p className="m-eyebrow">Where we help</p>
            <h2>Position Specialties</h2>
            <p>
              Our teams recruit and support the school staff who keep students
              learning and school days running.
            </p>
          </div>
          <div className="m-role-grid">
            {roles.map(item => (
              <div key={item}>{item}</div>
            ))}
          </div>
        </div>
      </section>
      <section className="m-section m-container">
        <div className="m-section__intro">
          <p className="m-eyebrow">The Spur difference</p>
          <h2>Benefits of Spur Staffing</h2>
          <p>
            Our people-focused team works to make education staffing more
            efficient and affordable.
          </p>
        </div>
        <div className="m-benefits">
          <div>
            <h3>Responsibly priced</h3>
            <p>
              Clear, responsible pricing helps districts invest more in the
              people and programs that matter.
            </p>
          </div>
          <div>
            <h3>Streamlined process</h3>
            <p>
              Manage requests, time, and staffing information from your computer
              or phone.
            </p>
          </div>
          <div>
            <h3>Engaged support</h3>
            <p>
              Our team is ready to help customers and employees with the tools
              and answers they need.
            </p>
          </div>
          <div>
            <h3>Increased satisfaction</h3>
            <p>
              Better access to work and a focus on employee experience help
              schools build dependable teams.
            </p>
          </div>
        </div>
      </section>
      <Team
        title="Education Staffing Success Team"
        text="Meet the people who recruit, support, and care for our school district partners and hourly employees."
        people={educationTeam}
      />
      <ContactForm />
    </>
  );
}

function Afterschool() {
  const services = [
    'Program Administration',
    'Standards-Aligned Curriculum',
    'Personnel Management',
    'Financial Management',
    'Student Achievement Data Analysis',
    'Supply Procurement',
    'Professional Development',
    'Payroll',
  ];
  return (
    <>
      <Hero
        eyebrow="Afterschool Labs"
        title="Accelerating student achievement through after-school and summer programs."
        text="Our turnkey, prescriptive approach to extended learning is designed to improve literacy and math proficiency, enhance engagement, and help students overcome learning loss."
        background="afterschool-hero.jpg"
        accent
      />
      <section id="learn-more" className="m-section m-container">
        <div className="m-section__intro">
          <p className="m-eyebrow m-eyebrow--orange">More time to learn</p>
          <h2>Extended Learning Services</h2>
          <p>
            Afterschool Labs manages every part of your after-school or summer
            program, from staffing and training to curriculum, supplies, and
            payroll.
          </p>
        </div>
        <div className="m-pill-grid m-pill-grid--orange">
          {services.map(item => (
            <div key={item}>{item}</div>
          ))}
        </div>
      </section>
      <section className="m-quote">
        <div className="m-container">
          <blockquote>
            “The data revealed that students who participated in the Afterschool
            Labs program had shown academic growth and higher attendance rates.”
          </blockquote>
          <p>Dr. Tanya Guin · Director of Elementary Education</p>
        </div>
      </section>
      <section className="m-section m-section--warm">
        <div className="m-container">
          <div className="m-section__intro">
            <p className="m-eyebrow m-eyebrow--orange">Why it matters</p>
            <h2>Impact of Afterschool Labs</h2>
          </div>
          <div className="m-benefits">
            <div>
              <h3>Accelerate student achievement</h3>
              <p>
                Targeted instruction and engaging activities help students
                strengthen reading and math skills.
              </p>
            </div>
            <div>
              <h3>Support teachers</h3>
              <p>
                Extra teaching and development opportunities can help educators
                grow and stay engaged.
              </p>
            </div>
            <div>
              <h3>Ease administrative work</h3>
              <p>
                Our team handles the daily operations so district leaders can
                focus on the regular school day.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Team
        title="Afterschool Labs Success Team"
        text="Educators and program specialists work with districts to deliver extended learning programs."
        people={afterschoolTeam}
      />
      <ContactForm />
    </>
  );
}

function Resources() {
  return (
    <>
      <div className="m-page-title m-container">
        <p className="m-eyebrow">Staffing resources</p>
        <h1>Everything you need to know about Spur Staffing.</h1>
        <p>
          Explore case studies, customer stories, and articles about working at
          and with Spur.
        </p>
      </div>
      <section className="m-section m-container">
        <div className="m-section__intro">
          <h2>Read About Us</h2>
          <p>
            See how our technology and people-focused approach support schools,
            staff, and students.
          </p>
        </div>
        <ArticleCards />
      </section>
      <section className="m-section m-section--lavender">
        <div className="m-container m-section__intro">
          <h2>Looking for worker support?</h2>
          <p>Search answers about jobs, onboarding, pay, benefits, and more.</p>
          <Button href="/worker-faq/">Search the Worker FAQ</Button>
        </div>
      </section>
      <section className="m-section m-section--lavender">
        <div className="m-container">
          <div className="m-section__intro">
            <h2>Hear About Us</h2>
            <p>School leaders share their experience with Afterschool Labs.</p>
          </div>
          <div className="m-video-grid">
            <a href="https://www.youtube.com/watch?v=NyvmlXpaj_k">
              Barbour County Schools <span>Closing the Achievement Gap →</span>
            </a>
            <a href="https://www.youtube.com/watch?v=_qjp3LLKkU0">
              Gadsden City Schools{' '}
              <span>Overcoming Student Learning Loss →</span>
            </a>
            <a href="https://www.youtube.com/watch?v=dv8kmlhbPug">
              Fort Payne City Schools <span>After-School Programs →</span>
            </a>
            <a href="https://www.youtube.com/watch?v=nvsYt11R-gs">
              Walker County Schools{' '}
              <span>Helping Students Overcome Deficiencies →</span>
            </a>
          </div>
        </div>
      </section>
      <ContactForm />
    </>
  );
}

function Contact() {
  return (
    <>
      <div className="m-page-title m-container">
        <p className="m-eyebrow">Get in touch</p>
        <h1>Let's start a conversation.</h1>
        <p>
          Tell us about your education staffing needs, or explore opportunities
          to join our team.
        </p>
        <div className="m-actions">
          <Button href="#contact">Contact Sales</Button>
          <Button href={jobs} outline>
            Join Our Team
          </Button>
        </div>
      </div>
      <ContactForm />
    </>
  );
}

function Article({ data }) {
  return (
    <>
      <div
        className="m-article-hero"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(22,20,31,.76),rgba(22,20,31,.2)), url(' +
            image(data.image) +
            ')',
        }}
      >
        <div className="m-container">
          <p className="m-eyebrow">{data.category}</p>
          <h1>{data.title}</h1>
          <p>{data.intro}</p>
        </div>
      </div>
      <article className="m-article-body m-container">
        <a className="m-text-link" href={url('/resources/')}>
          ← All resources
        </a>
        {data.sections.map(([heading, body]) => (
          <section key={heading}>
            <h2>{heading}</h2>
            <p>{body}</p>
          </section>
        ))}
        <div className="m-article-body__cta">
          <h2>Want to learn more?</h2>
          <p>
            Talk with our team about how Spur can support your school district.
          </p>
          <Button href="/contact-us/">Contact Us</Button>
        </div>
      </article>
    </>
  );
}

function Posters() {
  const states = [
    'Alabama',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'District of Columbia',
    'Florida',
    'Indiana',
    'Kentucky',
    'Maryland',
    'Massachusetts',
    'Mississippi',
    'New Hampshire',
    'New Mexico',
    'North Carolina',
    'Oklahoma',
    'Tennessee',
    'Washington',
    'Wisconsin',
  ];
  const slug = value => value.toLowerCase().replace(/ /g, '-');
  return (
    <section className="m-section m-container m-posters">
      <p className="m-eyebrow">Employee resources</p>
      <h1>Labor Law Posters</h1>
      <p>Select a location to view its labor law poster information.</p>
      <div className="m-role-grid">
        {states.map(state => (
          <a
            key={state}
            href={url(
              '/helpful-articles/' + slug(state) + '-labor-law-posters',
            )}
          >
            {state} →
          </a>
        ))}
      </div>
    </section>
  );
}

function Marketing({ page, slug }) {
  const article = articles.find(item => item.page === page);
  const titles = {
    home: 'Spur | Leading Innovation in Education Staffing',
    education: 'Education Staffing Services | Spur',
    afterschool: 'Afterschool Labs | Spur',
    resources: 'Resources | Spur',
    'worker-faq': 'Worker Support & FAQ | Spur',
    contact: 'Contact Us | Spur',
    posters: 'Labor Law Posters | Spur',
    privacy: 'Privacy Policy | Spur',
    terms: 'Terms of Service | Spur',
  };
  useEffect(() => {
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [page, slug]);
  return (
    <div className="marketing">
      <Helmet>
        <title>
          {article
            ? article.title + ' | Spur'
            : titles[page] || 'Spur Staffing'}
        </title>
      </Helmet>
      <a className="m-skip" href="#main">
        Skip to main content
      </a>
      <Header transparent={page === 'home'} />
      <main id="main">
        {page === 'home' ? (
          <Home />
        ) : page === 'education' ? (
          <Education />
        ) : page === 'afterschool' ? (
          <Afterschool />
        ) : page === 'resources' ? (
          <Resources />
        ) : page === 'worker-faq' ? (
          <WorkerFaq base={base} />
        ) : page === 'contact' ? (
          <Contact />
        ) : article ? (
          <Article data={article} />
        ) : page === 'posters' ? (
          <Posters />
        ) : page === 'privacy' ? (
          <div className="m-legacy m-container">
            <PrivacyPolicy />
          </div>
        ) : page === 'terms' ? (
          <div className="m-legacy m-container">
            <TermsOfService />
          </div>
        ) : page === 'helpful-articles' ? (
          <div className="m-legacy m-container">
            <HelpfulArticles />
          </div>
        ) : page === 'helpful-article' ? (
          <div className="m-legacy m-container">
            <HelpfulArticle slug={slug} />
          </div>
        ) : (
          <div className="m-page-title m-container">
            <h1>Page not found</h1>
            <p>We couldn't find that page.</p>
            <Button href="/">Back to home</Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Marketing;
